import { useEffect, useState } from "react";

interface Options {
  enabled?: boolean;
  storageKey?: string;
  mobileScrollThreshold?: number; // 0–1
  mobileMinTimeMs?: number;
}

/**
 * Fires once per session.
 * Desktop: pointerleave through the top edge of the viewport.
 * Mobile: scroll past N% AND on page for ≥ T ms, then trigger on any upward scroll.
 */
export function useExitIntent({
  enabled = true,
  storageKey = "exit_intent_fired",
  mobileScrollThreshold = 0.5,
  mobileMinTimeMs = 15000,
}: Options = {}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(storageKey) === "1") return;
    } catch {
      /* sessionStorage unavailable */
    }

    const trigger = () => {
      try { sessionStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
      setOpen(true);
      cleanup();
    };

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const startTs = Date.now();
    let lastScrollY = window.scrollY;

    const onPointerLeave = (e: PointerEvent) => {
      if (e.clientY <= 0) trigger();
    };

    const onScroll = () => {
      const elapsed = Date.now() - startTs;
      if (elapsed < mobileMinTimeMs) {
        lastScrollY = window.scrollY;
        return;
      }
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? window.scrollY / docH : 0;
      if (pct < mobileScrollThreshold) {
        lastScrollY = window.scrollY;
        return;
      }
      if (window.scrollY < lastScrollY - 40) {
        trigger();
      }
      lastScrollY = window.scrollY;
    };

    const cleanup = () => {
      document.documentElement.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("scroll", onScroll);
    };

    if (isMobile) {
      window.addEventListener("scroll", onScroll, { passive: true });
    } else {
      document.documentElement.addEventListener("pointerleave", onPointerLeave);
    }

    return cleanup;
  }, [enabled, storageKey, mobileScrollThreshold, mobileMinTimeMs]);

  return { open, setOpen };
}
